import { useState } from "react";
import { Check, SquareKanban, X } from "lucide-react";
import { useClearAdoPat, useMe, useSetAdoPat } from "@/hooks/queries/use-me";
import { useToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Settings section for the user's Azure DevOps personal access token
 * (PI-SPEC-SW-1359). Mirrors the FogBugz key section: the PAT is write-only
 * from the browser — the API stores it encrypted and only ever reports
 * `hasAdoPat`. Save and remove invalidate the `me` query, so the status (and
 * anything gated on `hasAdoPat`) updates without a reload.
 */
export function AdoPatSection() {
  const [pat, setPat] = useState("");
  const { data: meData, isLoading } = useMe();
  const setAdoPat = useSetAdoPat();
  const clearAdoPat = useClearAdoPat();
  const { toast } = useToast();

  const hasAdoPat = meData?.hasAdoPat ?? false;

  const handleSave = () => {
    const trimmed = pat.trim();
    if (!trimmed) return;
    setAdoPat.mutate(
      { pat: trimmed },
      {
        onSuccess: () => {
          setPat("");
          toast("Azure DevOps PAT saved");
        },
        onError: (err) => {
          toast(
            err instanceof Error
              ? `Failed to save Azure DevOps PAT: ${err.message}`
              : "Failed to save Azure DevOps PAT"
          );
        },
      }
    );
  };

  const handleClear = () => {
    clearAdoPat.mutate(undefined, {
      onSuccess: () => {
        toast("Azure DevOps PAT removed");
      },
      onError: (err) => {
        toast(
          err instanceof Error
            ? `Failed to remove Azure DevOps PAT: ${err.message}`
            : "Failed to remove Azure DevOps PAT"
        );
      },
    });
  };

  return (
    <div
      className="glass-card rounded-2xl border border-white/[0.08] p-6"
      data-testid="ado-pat-section"
    >
      <div className="flex items-center gap-2 mb-4">
        <SquareKanban className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-medium">Azure DevOps</h2>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
          Loading...
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {hasAdoPat ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                <span className="text-sm text-muted-foreground">PAT configured</span>
              </>
            ) : (
              <>
                <X className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">No PAT configured</span>
              </>
            )}
          </div>

          {hasAdoPat ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Your Azure DevOps PAT is securely stored and encrypted.
              </p>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleClear}
                disabled={clearAdoPat.isPending}
                className="w-full"
              >
                {clearAdoPat.isPending ? "Removing..." : "Remove PAT"}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="ado-pat" className="text-xs text-muted-foreground">
                Azure DevOps Personal Access Token
              </Label>
              <Input
                id="ado-pat"
                type="password"
                autoComplete="off"
                value={pat}
                onChange={(e) => {
                  setPat(e.target.value);
                }}
                placeholder="Enter your Azure DevOps PAT"
                className="bg-white/[0.04] border-white/[0.08]"
              />
              <p className="text-xs text-muted-foreground">
                Use an organisation-scoped PAT, scope Work Items (Read &amp; Write).
              </p>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!pat.trim() || setAdoPat.isPending}
                className="w-full"
              >
                {setAdoPat.isPending ? "Saving..." : "Save PAT"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
